import React, { useState, useEffect } from 'react';
import { Flex, Box } from 'reflexbox';
import { Form, RegularText, Button, SpinnerIcon, TableHead, TableRow, TableHeader, TableBody, TableData, Select, TableItemSkeleton, Table, SwitchButton, CheckIcon, CrossIcon, Tooltip } from 'boss-ui';
import { useEventContext } from '../../libs/context-lib';
import { toast } from 'react-toastify';
import { createPolling } from '../../libs/polling-lib';
import Api from '../../api';
import styled from 'styled-components';
import { onError } from '../../libs/error-lib';
import moment from 'moment/moment';

const NotificationTabOptions = [{
    value: 'Notification Center',
    label: 'Notification Center'
},
{
    value: 'Notification Logs',
    label: 'Notification Logs'
},
];

const Container = styled(Flex)`
    gap: 30px;
`;

const API = new Api();
const Admin = () => {
    const SEND_EMAIL_POST_EVENT = "Score emails can only be sent after the completion of the event";
    const SENDING_EMAILS_NOTIFICATION = "Initiated sending scores, it will take several seconds until all the emails are sent...";
    const { state } = useEventContext();
    const event = state.event;
    const [activityProgress, setActivityProgress] = useState({});
    const eventId = event.eventId;
    const [selectedTab, setSelectedTab] = useState(NotificationTabOptions[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [activityLogs, setActivityLogs] = useState([]);
    const [isTeamupTaskEnabled, setIsTeamupTaskEnabled] = useState(false);
    const startDateMoment = moment(event.startDate);
    const anHourBeforeStartDateMoment = startDateMoment.subtract(1, 'hour');
    const isSliderDisabled = moment().isSameOrAfter(anHourBeforeStartDateMoment);


    useEffect(() => {

        async function fetchActivityLogs() {
            await fetchNotificationLogs();
        };

        async function fetchTaskInformation() {
            await fetchTaskStatus();
        }

        if (selectedTab.value === "Notification Logs") {
            fetchActivityLogs();
        }

        if (selectedTab.value === "Notification Center") {
            fetchTaskInformation();
        }

    }, [selectedTab]); // Fetch logs when tab changes

    const fetchNotificationLogs = async () => {
        if (selectedTab.value === 'Notification Logs') {
            setIsLoading(true);
            try {
                const response = await API.get('events', `/admin/events/${eventId}/notification-activity`);
                response.activities.sort((a, b) => new Date(b.updatedOn) - new Date(a.updatedOn));
                setActivityLogs(response.activities);
            } catch (err) {
                onError(err);
                toast.error('Failed to fetch notification logs');
            } finally {
                setIsLoading(false);
            }
        }
    }

    const fetchTaskStatus = async () => {
        setIsLoading(true);
        try {
            setIsTeamupTaskEnabled(false);
            const response = await API.get('events', `/admin/events/${eventId}/teamup-task`);
            if (response !== undefined && response !== null) {
                setIsTeamupTaskEnabled(true);
            }else {
                toast.info('No Teamup Task is scheduled for this event');
                setIsTeamupTaskEnabled(false);
            }
        } catch (err) {
            onError(err);
            toast.error('Failed to fetch Task Status  logs');
        } finally {
            setIsLoading(false);
        }
    }


    const handleTabChange = async (tab) => {
        setSelectedTab(tab);
    }

    const isEventEnded = () => {
        return new Date(event.endDate) < new Date();
    }

    const sendScoresTrigger = async () => {
        try {
            setActivityProgress(prev => ({ ...prev, EMAIL_SCORE_ACTIVITY: true }));
            const eventBody = { eventId, type: 'EMAIL_SCORE_ACTIVITY' };
            const initialResponse = await API.post('events', `/admin/events/${eventId}/notification-activity`, { body: eventBody });
            if (initialResponse) {
                const processId = initialResponse.processId;
                const evalFunction = (res) => {
                    return res.activity.status === 'COMPLETED' || res.activity.status === 'FAILED';
                };
                const polling = createPolling('events', `/admin/events/${eventId}/notification-activity/${processId}`, { interval: 5000, timeout: 400000, checkFun: evalFunction });
                polling.promise.then((res) => {
                    toast('Scores sent successfully');
                    setActivityProgress(prev => ({ ...prev, EMAIL_SCORE_ACTIVITY: false }));
                }).catch((err) => {
                    console.log("Error while sending scores", err);
                    toast('Error while sending scores');
                    setActivityProgress(prev => ({ ...prev, EMAIL_SCORE_ACTIVITY: false }));
                });
            }
        } catch (e) {
            setActivityProgress(prev => ({ ...prev, EMAIL_SCORE_ACTIVITY: false }));
            onError(e);
        }
    };

    const sendScores = async () => {
        if (isEventEnded()) {
            toast(SENDING_EMAILS_NOTIFICATION);
            await sendScoresTrigger();
        } else {
            toast(SEND_EMAIL_POST_EVENT);
        }
    };

    const updateTeamupTaskStatus = async () => {
        setIsLoading(true);
        try {
            const updatedState = !isTeamupTaskEnabled;
            const task = updatedState ? 'CREATE_TASK' : 'DELETE_TASK';
            const eventBody = { eventId, task };
            await API.post('events', `/admin/events/${eventId}/teamup-task`, { body: eventBody });
            setIsTeamupTaskEnabled(updatedState);
        } catch (err) {
            toast.error('Failed to update task status');
        } finally {
            setIsLoading(false);
        }
    };


    const activityNames = [{
        activityId: "EMAIL_SCORE_ACTIVITY",
        label: "Send Score Mails",
        description: "Send score mails to all participants",
        action: sendScores
    }];

    const NotificationActivityTab = () => {
        return (
            <Container mt="30px" mb="5%" flexDirection="column">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>
                                <RegularText fontSize="14px" >Task</RegularText>
                            </TableHeader>
                            <TableHeader>
                                <RegularText fontSize="14px" >Description</RegularText>
                            </TableHeader>
                            <TableHeader>
                                <RegularText fontSize="14px" >Status</RegularText>
                            </TableHeader>
                        </TableRow>
                    </TableHead>


                    <TableBody>
                        {activityNames.map((activity, index) => (

                            <TableRow key={index}>
                                <TableData>
                                    <Button onClick={activity.action} disabled={activityProgress[activity.activityId]}>
                                        {activity.label}
                                    </Button>
                                </TableData>
                                <TableData>
                                    <RegularText fontSize="11px" ml="22px">
                                        {activity.description}
                                    </RegularText>
                                </TableData>
                                <TableData>
                                    {activityProgress[activity.activityId] ? (
                                        <Box>
                                            <SpinnerIcon size="18px" />
                                            <RegularText fontSize="12px" ml="8px">{`In Progress`}</RegularText>
                                        </Box>
                                    ) : (
                                        <RegularText fontSize="12px" ml="8px">Idle</RegularText>
                                    )}

                                </TableData>
                            </TableRow>
                        )
                        )}
                    </TableBody>
                </Table>
                <Flex justifyContent="center">
                    <RegularText mediumWeight>Scheduled Tasks</RegularText>
                </Flex>
                {isLoading ? <TableItemSkeleton numberOfCells={4} /> :
                    (<Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>
                                    <RegularText fontSize="14px" >Task</RegularText>
                                </TableHeader>
                                <TableHeader>
                                    <RegularText fontSize="14px" >Description</RegularText>
                                </TableHeader>
                                <TableHeader>
                                    <RegularText fontSize="14px" >Status</RegularText>
                                </TableHeader>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            <TableRow>
                                <TableData>
                                    <RegularText fontSize="14px">
                                        Team Reminder
                                    </RegularText>
                                </TableData>
                                <TableData>
                                    <RegularText fontSize="14px">
                                        Send a reminder email to individual participants to team up 1Hr before the event starts.
                                    </RegularText>
                                </TableData>
                                <TableData>
                                    <Flex data-tip data-for="teamupTaskStatus">
                                    <SwitchButton
                                    data-for="teamupTaskStatus"
                                        disabled={isSliderDisabled}
                                        checked={isTeamupTaskEnabled}
                                        handleToggle={updateTeamupTaskStatus}
                                        id="visibility"
                                        onText="Enabled"
                                        offText="Disabled"
                                        onIcon={<CheckIcon />}
                                        offIcon={<CrossIcon />}
                                    />
                                    </Flex>
                                      <Tooltip id="teamupTaskStatus">
                                            {isTeamupTaskEnabled ? 'Enabled' : 'Disabled' + (!isSliderDisabled ? '' : '(Locked 1 hour before event start)')}
                                    </Tooltip>                       
                                </TableData>
                            </TableRow>
                        </TableBody>
                    </Table>)}
            </Container>
        );
    }

    const NotificationActivityLogsTab = () => {
        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>
                            <RegularText>Activity Name</RegularText>
                        </TableHeader>
                        <TableHeader>
                            <RegularText>Activity Status</RegularText>
                        </TableHeader>
                        <TableHeader>
                            <RegularText>Ran At</RegularText>
                        </TableHeader>
                        <TableHeader>
                            <RegularText>Process Id</RegularText>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading ? <TableItemSkeleton numberOfCells={4} /> :

                        (
                            activityLogs.map((activity, index) => (
                                <TableRow key={index}>
                                    <TableData>
                                        <RegularText>{activity.type}</RegularText>
                                    </TableData>
                                    <TableData>
                                        <RegularText>{activity.status}</RegularText>
                                    </TableData>
                                    <TableData>
                                        <RegularText>{new Date(activity.createdOn).toUTCString()}</RegularText>
                                    </TableData>
                                    <TableData>
                                        <RegularText>{activity.processId}</RegularText>
                                    </TableData>
                                </TableRow>
                            ))
                        )


                    }
                </TableBody>
            </Table>
        );
    }

    const RenderTabContent = () => {
        switch (selectedTab) {
            case NotificationTabOptions[0]:
                return <NotificationActivityTab />;
            case NotificationTabOptions[1]:
                return <NotificationActivityLogsTab />;
            default:
                return <NotificationActivityTab />;
        }
    }



    return (
        <Flex mb="5%" mt="60px" justifyContent="center">
            <Form noBorder>
                <Box px="60px" py="40px" width="924px">
                    <Flex p="40px" pb="30px" flexDirection="column">
                        <Flex justifyContent="center">
                            <RegularText mediumWeight>{selectedTab.value}</RegularText>
                        </Flex>
                        <Flex width={1}>
                            <Box width={1 / 2}>
                                <Flex justifyContent="center" alignItems="center" width="240px" mt="20px" mb="20px">
                                    <Select options={NotificationTabOptions} value={selectedTab} onChange={handleTabChange} />
                                </Flex>
                            </Box>
                        </Flex>
                        <Flex mt="20px" alignItems="center" width="100%">
                            <RenderTabContent />
                        </Flex>
                    </Flex>
                </Box>
            </Form>
        </Flex>
    );
}

export default Admin;
